import dayjs from "dayjs";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { MoreOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { StringParam, NumberParam, withDefault, useQueryParams } from "use-query-params";
import { Select, Button, Dropdown, DatePicker, Typography, ConfigProvider,Row, Col, Input, Table, Modal, Empty } from "antd";

import type { ColumnsType } from "antd/es/table";
import type { RangePickerProps } from "antd/es/date-picker";

import { Generic } from "components/Status";
import ErrorFallback from "components/ErrorFallback";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { BatchSummaryInput } from "models";
import { openNotificationWithIcon } from "utils";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useGetBatchStatusEnumQuery } from "app/configApiSlice";
import {
  updateLayout,
  setIsRequeryFinalStatusModalOpen,
  setIsInvalidateFinalStatusModalOpen,
  setIsRevalidateFinalStatusModalOpen,
} from "pages/Generation/generationSlice";
import {
  useSearchSummaryBatchQuery,
  useRequeryBatchFinalStatusMutation,
  useRevalidateBatchFinalStatusMutation,
  useInvalidateBatchFinalStatusMutation,
} from "pages/Generation/generationApiSlice";

const { Option } = Select;

interface IRangePickerProps {
  endDate?: string;
  onOpen?: boolean;
  startDate?: string;
}

const BatchTableContainer = styled.div`
  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 4px;
    col {
      border-radius: 4px;
    }
  }
`;

const GenerationBatchTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    isRequeryFinalStatusModalOpen,
    isInvalidateFinalStatusModalOpen,
    isRevalidateFinalStatusModalOpen,
  } = useAppSelector((state) => state.generation);

  const [batchId, setBatchId] = useState<string>("");
  const [rangePickerProps, setRangePickerProps] = useState<IRangePickerProps>();

  const [batchSummaryParams, setBatchSummaryParams] = useQueryParams({
    page: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 10),
    status: withDefault(StringParam, ""),
    batchId: withDefault(StringParam, ""),
    endExtractionDate: withDefault(StringParam, ""),
    startExtractionDate: withDefault(StringParam, ""),
  });

  const batchStatusEnum = useGetBatchStatusEnumQuery();
  const [requeryFinalStatus, requeryFinalStatusResult] = useRequeryBatchFinalStatusMutation();
  const [invalidateFinalStatus, invalidateFinalStatusResult] =
    useInvalidateBatchFinalStatusMutation();
  const [revalidateFinalStatus, revalidateFinalStatusResult] =
    useRevalidateBatchFinalStatusMutation();
  const summaryBatch = useSearchSummaryBatchQuery(batchSummaryParams as any);

  const handleTableChange = async (pagination: any, filters: any, sorter: any) => {
    setBatchSummaryParams({
      page: pagination.current - 1,
      size: pagination.pageSize,
    });
  };

  const handleFilterChange = (
    name: string = "",
    value: {
      key?: string;
      label?: string;
      value?: string;
      disabled?: boolean;
    },
  ) => {
    setBatchSummaryParams({ [name]: value.value, page: 0 });
  };

  const handleInvalidateFinalStatus = (batchId: string) => {
    invalidateFinalStatus(batchId)
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon("Invalidate final status", payload.response, "success");
      })
      .catch((error) => {
        if (error.originalStatus >= 500 && error.originalStatus < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error.status === 400) {
          error.data?.errorMessageList?.length
            ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
                openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      })
      .finally(() => {
        dispatch(setIsInvalidateFinalStatusModalOpen(false));
      });
  };

  const handleRevalidateFinalStatus = (batchId: string) => {
    revalidateFinalStatus(batchId)
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon("Revalidate final status", payload.response, "success");
      })
      .catch((error) => {
        if (error.originalStatus >= 500 && error.originalStatus < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error.status === 400) {
          error.data?.errorMessageList?.length
            ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
                openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      })
      .finally(() => {
        dispatch(setIsRevalidateFinalStatusModalOpen(false));
      });
  };

  const handleRequeryFinalStatus = (batchId: string) => {
    requeryFinalStatus(batchId)
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon("Requery final status", payload.response, "success");
      })
      .catch((error) => {
        if (error.originalStatus >= 500 && error.originalStatus < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error.status === 400) {
          error.data?.errorMessageList?.length
            ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
                openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      })
      .finally(() => {
        dispatch(setIsRequeryFinalStatusModalOpen(false));
      });
  };

  useEffect(() => {
    dispatch(
      updateLayout({
        title: "Generation - Transaction Batch",
        function: "HOME",
        path: "",
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (rangePickerProps?.onOpen) {
      handleFilterChange("startExtractionDate", {
        value: rangePickerProps.startDate,
      });
      handleFilterChange("endExtractionDate", {
        value: rangePickerProps.endDate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangePickerProps]);

  const columns: ColumnsType<BatchSummaryInput> = [
    {
      width: "12%",
      key: "batchID",
      title: "Batch ID",
      dataIndex: "batchID",
      render: (text: string, record) => (
        <Button
          type="link"
          onClick={() => navigate("/t-plus-one-processing")}
          disabled={
            !(
              record.crBatchStatus === "EXTRACTED" &&
              record.drBatchStatus === "EXTRACTED" &&
              record.status === "EXTRACTED"
            )
          }
          style={{
            margin: 0,
            height: 0,
            padding: 0,
            fontSize: 14,
          }}
        >
          {text}
        </Button>
      ),
    },

    {
      width: "15%",
      title: "Expected Credit",
      key: "expectedTotalCredit",
      dataIndex: "expectedTotalCredit",
      align: "right",
    },

    {
      width: "15%",
      title: "Expected Debit",
      key: "expectedTotalCredit",
      dataIndex: "expectedTotalDebit",
      align: "right",
    },

    {
      width: "20%",
      key: "drBatchStatus",
      title: "Debit Status",
      dataIndex: "drBatchStatus",
      align: "right",
      render: (value) => <Generic status={value} />,
    },

    {
      width: "15%",
      key: "crBatchStatus",
      title: "Credit Status",
      dataIndex: "crBatchStatus",
      align: "right",
      render: (value) => <Generic status={value} />,
    },
    {
      width: "15%",
      key: "status",
      title: "Batch Status",
      dataIndex: "status",
      align: "right",
      render: (value) => <Generic status={value} />,
    },

    {
      title: "Action",
      width: "15%",
      dataIndex: "action",
      key: "action",
      align: "center",
      render: (text: string, record) => {
        return (
          <Dropdown
          menu={{items: [
            {
              key: 0,
              disabled:
                record.status === "NOT_VALID" ||
                (record.crBatchStatus === "EXTRACTED" &&
                  record.drBatchStatus === "EXTRACTED" &&
                  (record.status === "EXTRACTED" || record.status === "PUSHED")),
              label: (
                <Button
                  type="link"
                  style={{ width: "100%", textAlign: "left" }}
                  disabled={
                    (record.status !== "EXTRACTED_WITH_ERROR" &&
                      record.status !== "EXTRACTED") ||
                    (record.crBatchStatus === "EXTRACTED" &&
                      record.drBatchStatus === "EXTRACTED")
                  }
                  onClick={() => navigate(`/generation/edit/${record.batchID}`)}
                >
                  Edit
                </Button>
              ),
            },

            {
              key: 1,
              disabled: record.status === "PUSHED" || record.status === "NOT_VALID",

              label: (
                <Button
                  type="link"
                  style={{ width: "100%", textAlign: "left" }}
                  disabled={record.status === "PUSHED" || record.status === "NOT_VALID"}
                  onClick={() => {
                    setBatchId(record.batchID);
                    dispatch(setIsInvalidateFinalStatusModalOpen(true));
                  }}
                >
                  Invalidate
                </Button>
              ),
            },
            {
              key: 2,
              disabled: record.status !== "NOT_VALID",
              label: (
                <Button
                  type="link"
                  style={{ width: "100%", textAlign: "left" }}
                  disabled={record.status !== "NOT_VALID"}
                  onClick={() => {
                    setBatchId(record.batchID);
                    dispatch(setIsRevalidateFinalStatusModalOpen(true));
                  }}
                >
                  Revalidate
                </Button>
              ),
            },

            {
              key: 3,
              disabled: record.status !== "PUSHED",
              label: (
                <Button
                  type="link"
                  style={{ width: "100%", textAlign: "left" }}
                  disabled={record.status !== "PUSHED"}
                  onClick={() => {
                    setBatchId(record.batchID);
                    dispatch(setIsRequeryFinalStatusModalOpen(true));
                  }}
                >
                  Requery
                </Button>
              ),
            },
          ]}}
          
            placement="bottomRight"
            trigger={["click"]}
          >
            <MoreOutlined
              // onClick={}
              style={{
                fontSize: "22px",
                cursor: "pointer",
                color: "#1890ff",
                background: "transparent",
                margin: 0,
                padding: 0,
              }}
            />
          </Dropdown>
        );
      },
    },
  ];

  const disabledGenerationDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current > dayjs().endOf("day");
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Helmet>
        <title>NEFT Settlement | Generation</title>
        <meta name="description" content="Helmet application" />
      </Helmet>
      <BatchTableContainer>
        <Row justify="space-between" style={{ padding: "15px 0px" }}>
          <Col>
            <Row gutter={10}>
              <Col>
                <Select
                  size="large"
                  labelInValue={true}
                  suffixIcon=" STATUS"
                  defaultValue={{
                    value: batchSummaryParams?.status || "",
                  }}
                  notFoundContent={
                    <Button onClick={() => batchStatusEnum.refetch()}>reload</Button>
                  }
                  onChange={(value: {
                    key?: string;
                    label?: string;
                    value?: string;
                    disabled?: boolean;
                  }) => handleFilterChange("status", value)}
                  style={{ width: 220 }}
                >
                  {batchStatusEnum.isSuccess ? (
                    <>
                      <Option value={""}>All</Option>
                      {batchStatusEnum.data?.response?.map(({ code, description }) => (
                        <Option value={code}>{description}</Option>
                      ))}
                    </>
                  ) : null}
                </Select>
              </Col>

              <Col>
                <DatePicker
                  size="large"
                  format="YYYY-MM-DD"
                  disabledDate={disabledGenerationDate}
                  defaultValue={
                    batchSummaryParams?.startExtractionDate
                      ? (dayjs(batchSummaryParams?.startExtractionDate) as any)
                      : undefined
                  }
                  suffixIcon={<div style={{ fontSize: 12 }}>EXTRACTED DATE</div>}
                  onOpenChange={(value) => {
                    if (value) {
                      setRangePickerProps((prev) => ({
                        ...prev,
                        onOpen: value,
                      }));
                    }
                  }}
                  onChange={(dates, dateString) => {
                    setRangePickerProps((prev) => ({
                      ...prev,
                      startDate: dateString,
                      endDate: dateString,
                    }));
                  }}
                />
              </Col>
            </Row>
          </Col>

          <Col>
            <Input.Search
              allowClear
              addonBefore="BatchID"
              onSearch={(value) =>
                handleFilterChange("batchId", {
                  value: value,
                })
              }
              style={{ width: "auto" }}
              enterButton
              size="large"
              defaultValue=""
              placeholder="Enter Batch ID"
            />
          </Col>
        </Row>

        <ConfigProvider
          renderEmpty={() =>
            summaryBatch.isError ? (
              <ApiErrorTableComponent queryResult={summaryBatch} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }
        >
          <Table
            key="uid"
            columns={columns as any}
            onChange={handleTableChange}
            rowKey={(record: any) => record?.id}
            loading={summaryBatch.isFetching}
            dataSource={summaryBatch?.isError ? [] : summaryBatch.data?.response.content}
            pagination={{
              current:
                summaryBatch.data?.response?.number === 0
                  ? 1
                  : summaryBatch.data?.response?.number && summaryBatch.data?.response?.number + 1,
              showTitle: true,
              showSizeChanger: true,
              showQuickJumper: true,
              showPrevNextJumpers: true,
              pageSize: summaryBatch.data?.response.size,
              pageSizeOptions: [5, 10, 20, 50, 100, 200],
              total: summaryBatch.data?.response?.totalElements,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </ConfigProvider>

        <Modal
          okText="Yes"
          maskClosable={false}
          title="Invalidate Batch Status"
          visible={isInvalidateFinalStatusModalOpen}
          onOk={() => handleInvalidateFinalStatus(batchId)}
          closable={!invalidateFinalStatusResult.isLoading}
          confirmLoading={invalidateFinalStatusResult.isLoading}
          onCancel={() => dispatch(setIsInvalidateFinalStatusModalOpen(false))}
          cancelButtonProps={{
            disabled: invalidateFinalStatusResult.isLoading,
          }}
        >
          <Typography.Text style={{ fontSize: "14px" }}>
            Are you sure you want to invalidate the batch with ID: {batchId}.
          </Typography.Text>
        </Modal>

        <Modal
          okText="Yes"
          maskClosable={false}
          title="Revalidate Batch Status"
          visible={isRevalidateFinalStatusModalOpen}
          onOk={() => handleRevalidateFinalStatus(batchId)}
          closable={!revalidateFinalStatusResult.isLoading}
          confirmLoading={revalidateFinalStatusResult.isLoading}
          onCancel={() => dispatch(setIsRevalidateFinalStatusModalOpen(false))}
          cancelButtonProps={{
            disabled: revalidateFinalStatusResult.isLoading,
          }}
        >
          <Typography.Text style={{ fontSize: "14px" }}>
            Are you sure you want to revalidate the batch with ID: {batchId}.
          </Typography.Text>
        </Modal>

        <Modal
          okText="Yes"
          maskClosable={false}
          title="Holiday: Requery Batch Transaction Statuses"
          visible={isRequeryFinalStatusModalOpen}
          onOk={() => handleRequeryFinalStatus(batchId)}
          closable={!requeryFinalStatusResult.isLoading}
          confirmLoading={requeryFinalStatusResult.isLoading}
          onCancel={() => dispatch(setIsRequeryFinalStatusModalOpen(false))}
          cancelButtonProps={{
            disabled: requeryFinalStatusResult.isLoading,
          }}
        >
          <Typography.Text style={{ fontSize: "14px" }}>
            Requery the final transaction statuses of the batch with ID: {batchId}. This is
            typically used after a weekend or holiday period.
          </Typography.Text>
        </Modal>
      </BatchTableContainer>
    </ErrorBoundary>
  );
};

export default GenerationBatchTable;
